import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Button, IconButton, Surface } from '@americanexpress/dls-react';
import {
  IconClose,
} from '@americanexpress/dls-icons';
import { useRoomsContext } from '../../contexts/roomsContext';
import Room from '../Room/Room';
import AddRoomButton from '../AddRoomButton/AddRoomButton';
import TooltipFooter from './TooltipFooter';
import MobileTooltipHeader from './MobileTooltipHeader';
import TooltipBody from './TooltipBody';
import { useEscape } from '../../hooks/useEscape';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useConfigContext } from '../../contexts/configContext';
import styles from './Tooltip.scss';

const RoomsTooltip = memo(({ tooltipTriggerRef, tooltipPopoverRef }) => {
  const {
    rooms,
    setTooltipVisible,
    tooltipVisible,
  } = useRoomsContext();
  const { hasRooms, maxRooms, moduleId } = useConfigContext();

  const { formatMessage } = useIntl();

  const refocusOnTrigger = useCallback(() => {
    tooltipTriggerRef.current.focus();
  }, [tooltipTriggerRef]);

  const closeRoomsDropdownAndRefocus = useCallback(() => {
    /* istanbul ignore next - this line is covered by Playwright at
    /__tests__/playwright/tooltip.spec.ts */
    if (tooltipVisible) {
      setTooltipVisible(false);
      refocusOnTrigger();
    }
  }, [refocusOnTrigger, setTooltipVisible, tooltipVisible]);

  useEscape(closeRoomsDropdownAndRefocus, []);

  useOnClickOutside(
    [tooltipPopoverRef, tooltipTriggerRef],
    closeRoomsDropdownAndRefocus
  );
  const isMultiRoom = hasRooms && maxRooms > 1 && rooms.length <= maxRooms;

  if (!tooltipVisible) {
    return null;
  }
  return (
    <Surface
      className={clsx(styles.tooltip, 'flex flex-column shadow-3')}
      id="Tooltip window"
      variant="foreground"
      ref={tooltipPopoverRef}
    >
      <MobileTooltipHeader classNames="flex-justify-end">
        <IconButton
          screenReaderLabel={formatMessage({ id: 'mobileTooltipClose' })}
          id={`${moduleId}_closeButton`}
          onClick={closeRoomsDropdownAndRefocus}
          variant="tertiary"
        >
          <IconClose size="md" />
        </IconButton>
      </MobileTooltipHeader>
      <TooltipBody>
        {rooms.map((_, roomIndex) => (
          <Room
            canRemove={rooms.length > 1}
            isMultiRoom={isMultiRoom}
            key={`room-${roomIndex + 1}`}
            roomIndex={roomIndex}
          />
        ))}
        <AddRoomButton />
      </TooltipBody>
      <TooltipFooter>
        <Button
          id={`${moduleId}_doneButton`}
          onClick={closeRoomsDropdownAndRefocus}
          variant="primary"
        >
          {formatMessage({ id: 'done' })}
        </Button>
      </TooltipFooter>
    </Surface>
  );
});

RoomsTooltip.displayName = 'RoomsTooltip';

RoomsTooltip.propTypes = {
  tooltipTriggerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.object]),
  tooltipPopoverRef: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.object]),
};

export { RoomsTooltip as TestableRoomsTooltip };

export default RoomsTooltip;
